// src/components/admin/GoogleReviewManagement.js
import {
  AlertTriangle,
  BarChart3,
  Filter,
  Flag,
  RefreshCw,
  Search,
  Star,
  ThumbsUp,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

const GoogleReviewManagement = () => {
  // State management
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    overall: { averageRating: 0, totalReviews: 0, distribution: {} },
    recent: { totalReviews: 0 },
    sentiment: { positive: 0, neutral: 0, negative: 0 },
  });

  // Filter and pagination states
  const [filters, setFilters] = useState({
    rating: "",
    status: "active",
    sentiment: "",
    search: "",
    source: "google",
    timeframe: "30",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // UI states
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("reviews");
  const [responseModal, setResponseModal] = useState({
    show: false,
    reviewId: null,
    text: "",
    mode: "add", // 'add' or 'edit'
  });
  const [noteModal, setNoteModal] = useState({
    show: false,
    reviewId: null,
    note: "",
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    action: null,
    reviewId: null,
  });
  const [reportModal, setReportModal] = useState({
    show: false,
    reviewId: null,
    reason: "inappropriate_content",
  });

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Clear any existing errors
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });

      const response = await fetch(
        `/api/googleReviews/admin/reviews?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch reviews");
      }

      const data = await response.json();
      setReviews(data.reviews);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      }));
    } catch (err) {
      setError(err.message);
      setReviews([]); // Clear reviews when there's an error
      setPagination((prev) => ({
        ...prev,
        total: 0,
        totalPages: 0,
      }));
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/analytics?timeframe=${filters.timeframe}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
  }, [filters.timeframe]);

  // Initial load
  useEffect(() => {
    fetchReviews();
    fetchAnalytics();
  }, [fetchReviews, fetchAnalytics]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle review selection
  const toggleReviewSelection = (reviewId) => {
    setSelectedReviews((prev) =>
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const selectAllReviews = () => {
    setSelectedReviews(
      selectedReviews.length === reviews.length ? [] : reviews.map((r) => r._id)
    );
  };

  // Update review status
  const updateReviewStatus = async (reviewId, updates) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${reviewId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update review");
      }

      await fetchReviews();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Delete/Archive review
  const deleteReview = async (reviewId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${reviewId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete review");
      }

      await fetchReviews();
      setConfirmModal({ show: false });
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Report review to Google
  const reportReview = async (reviewId, reason) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${reviewId}/report`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to report review");
      }

      setReportModal({
        show: false,
        reviewId: null,
        reason: "inappropriate_content",
      });
      await fetchReviews();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Add response to review
  const addResponse = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${responseModal.reviewId}/respond`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            responseText: responseModal.text,
            isPublic: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to add response");
      }

      setResponseModal({ show: false, reviewId: null, text: "", mode: "add" });
      await fetchReviews();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Add internal note
  const addNote = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${noteModal.reviewId}/notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            note: noteModal.note,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to add note");
      }

      setNoteModal({ show: false, reviewId: null, note: "" });
      await fetchReviews();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Add function to handle deleting a reply
  const deleteReply = async (reviewId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${reviewId}/respond`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            responseText: "",
            isPublic: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete reply");
      }

      await fetchReviews();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Render star rating
  const renderStars = (rating, size = "w-4 h-4") => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  // Get sentiment color
  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "text-green-600 bg-green-100";
      case "negative":
        return "text-red-600 bg-red-100";
      default:
        return "text-yellow-600 bg-yellow-100";
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "hidden":
        return "text-gray-600 bg-gray-100";
      case "flagged":
        return "text-red-600 bg-red-100";
      case "archived":
        return "text-purple-600 bg-purple-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // Add function to open response modal
  const openResponseModal = (review, mode = "add") => {
    setResponseModal({
      show: true,
      reviewId: review._id,
      text: mode === "edit" ? review.adminResponse?.text || "" : "",
      mode,
    });
  };

  // Add function to handle response submission
  const handleResponseSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/googleReviews/admin/reviews/${responseModal.reviewId}/respond`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            responseText: responseModal.text,
            isPublic: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${responseModal.mode} response`);
      }

      setResponseModal({ show: false, reviewId: null, text: "", mode: "add" });
      await fetchReviews();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Google Reviews Management
          </h2>

          <p className="text-gray-600 mt-1">
            Manage your Google reviews, responses, and public visibility
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
          <button
            onClick={fetchReviews}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <p>{error}</p>
          <button
            onClick={() => setError(null)}
            className="float-right font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Star className="h-8 w-8 text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Average Rating
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.overall.averageRating.toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Reviews</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.overall.totalReviews}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ThumbsUp className="h-8 w-8 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Positive</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.sentiment.positive}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Recent</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.recent.totalReviews}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Search reviews..."
                  className="pl-10 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating
              </label>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange("rating", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
                <option value="flagged">Flagged</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sentiment
              </label>
              <select
                value={filters.sentiment}
                onChange={(e) =>
                  handleFilterChange("sentiment", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sentiment</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timeframe
              </label>
              <select
                value={filters.timeframe}
                onChange={(e) =>
                  handleFilterChange("timeframe", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="365">Last year</option>
                <option value="">All time</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({
                    rating: "",
                    status: "active",
                    sentiment: "",
                    search: "",
                    source: "google",
                    timeframe: "30",
                  });
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedReviews.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm text-blue-700">
                {selectedReviews.length} review(s) selected
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  selectedReviews.forEach((id) =>
                    updateReviewStatus(id, { isPublic: false })
                  );
                  setSelectedReviews([]);
                }}
                className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                Hide Selected
              </button>
              <button
                onClick={() => {
                  selectedReviews.forEach((id) =>
                    updateReviewStatus(id, { isPublic: true })
                  );
                  setSelectedReviews([]);
                }}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Show Selected
              </button>
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to archive ${selectedReviews.length} reviews?`
                    )
                  ) {
                    selectedReviews.forEach((id) => deleteReview(id));
                    setSelectedReviews([]);
                  }
                }}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Archive Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Reviews ({error ? 0 : pagination.total})
            </h3>
            {!error && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={
                    selectedReviews.length === reviews.length &&
                    reviews.length > 0
                  }
                  onChange={selectAllReviews}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-500">Select All</span>
              </div>
            )}
          </div>
        </div>

        {error ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
            <button
              onClick={fetchReviews}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {reviews.map((review) => (
              <div
                key={review._id}
                className={`p-6 hover:bg-gray-50 ${
                  review.status === "flagged" ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-grow">
                    <input
                      type="checkbox"
                      checked={selectedReviews.includes(review._id)}
                      onChange={() => toggleReviewSelection(review._id)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />

                    <div className="flex-shrink-0">
                      {review.authorPhotoUrl ? (
                        <img
                          src={review.authorPhotoUrl}
                          alt={review.authorName}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm text-gray-700">
                            {review.authorName?.charAt(0).toUpperCase() || "?"}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            {review.authorName}
                            {renderStars(review.rating)}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          {review.status === "flagged" ? (
                            <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-1 rounded-md">
                              <Flag className="w-4 h-4" />
                              <span className="text-sm">Flagged</span>
                            </div>
                          ) : review.status === "archived" ? (
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you want to restore this review to active status?"
                                  )
                                ) {
                                  updateReviewStatus(review._id, {
                                    status: "active",
                                    isPublic: true,
                                  });
                                }
                              }}
                              className="px-3 py-1 text-sm rounded-md bg-green-50 text-green-600 hover:bg-green-100 transition-colors flex items-center"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Restore to Active
                            </button>
                          ) : review.status === "hidden" ? (
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you want to make this review active?"
                                  )
                                ) {
                                  updateReviewStatus(review._id, {
                                    status: "active",
                                    isPublic: true,
                                  });
                                }
                              }}
                              className="px-3 py-1 text-sm rounded-md bg-green-50 text-green-600 hover:bg-green-100 transition-colors flex items-center"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Make Active
                            </button>
                          ) : (
                            review.status === "active" && (
                              <>
                                {review.adminResponse?.text ? (
                                  <>
                                    <button
                                      onClick={() =>
                                        openResponseModal(review, "edit")
                                      }
                                      className="px-3 py-1 text-sm rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                    >
                                      Edit Reply
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            "Are you sure you want to delete this reply?"
                                          )
                                        ) {
                                          deleteReply(review._id);
                                        }
                                      }}
                                      className="px-3 py-1 text-sm rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                    >
                                      Delete Reply
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() =>
                                      openResponseModal(review, "add")
                                    }
                                    className="px-3 py-1 text-sm rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                  >
                                    Add Reply
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    setReportModal({
                                      show: true,
                                      reviewId: review._id,
                                      reason: "inappropriate_content",
                                    })
                                  }
                                  className="px-3 py-1 text-sm rounded-md bg-yellow-50 text-yellow-600 hover:bg-yellow-100 transition-colors flex items-center"
                                >
                                  <Flag className="w-4 h-4 mr-1" />
                                  Report
                                </button>
                              </>
                            )
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {review.text}
                      </p>
                      {(review.adminResponse?.text ||
                        review.metadata?.aiResponse) && (
                        <div className="mt-2 pl-4 border-l-2 border-blue-200">
                          <p className="text-sm text-gray-600">
                            {review.adminResponse?.text ||
                              review.metadata?.aiResponse}
                            {review.metadata?.aiResponse && (
                              <span className="ml-2 text-xs text-gray-400">
                                (AI Generated)
                              </span>
                            )}
                          </p>
                          {review.adminResponse?.text &&
                            review.adminResponse?.respondedBy && (
                              <p className="text-xs text-gray-500 mt-1">
                                By{" "}
                                {review.adminResponse.respondedBy?.firstName ||
                                review.adminResponse.respondedBy?.lastName ? (
                                  <>
                                    {
                                      review.adminResponse.respondedBy
                                        ?.firstName
                                    }{" "}
                                    {review.adminResponse.respondedBy?.lastName}
                                  </>
                                ) : (
                                  "Admin"
                                )}{" "}
                                on{" "}
                                {new Date(
                                  review.adminResponse.respondedAt
                                ).toLocaleDateString()}
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Only show pagination if there's no error and we have reviews */}
        {!error && reviews.length > 0 && (
          <div className="flex justify-between items-center p-4">
            <span className="text-sm text-gray-700">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.min(prev.totalPages, prev.page + 1),
                }))
              }
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Response Modal */}
      {responseModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              {responseModal.mode === "edit" ? "Edit Reply" : "Add Reply"}
            </h3>
            <textarea
              value={responseModal.text}
              onChange={(e) =>
                setResponseModal((prev) => ({
                  ...prev,
                  text: e.target.value,
                }))
              }
              placeholder="Enter your response to this review..."
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() =>
                  setResponseModal({
                    show: false,
                    reviewId: null,
                    text: "",
                    mode: "add",
                  })
                }
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResponseSubmit}
                disabled={!responseModal.text.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {responseModal.mode === "edit" ? "Update Reply" : "Add Reply"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {noteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Add Internal Note</h3>
            <textarea
              value={noteModal.note}
              onChange={(e) =>
                setNoteModal((prev) => ({ ...prev, note: e.target.value }))
              }
              placeholder="Enter internal note (only visible to admins)..."
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() =>
                  setNoteModal({ show: false, reviewId: null, note: "" })
                }
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addNote}
                disabled={!noteModal.note.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                {confirmModal.title}
              </h3>
            </div>
            <p className="text-gray-700 mb-6">{confirmModal.message}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmModal({ show: false })}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmModal.action) {
                    confirmModal.action();
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {reportModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <Flag className="w-6 h-6 text-yellow-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Report Review
              </h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Report
              </label>
              <select
                value={reportModal.reason}
                onChange={(e) =>
                  setReportModal((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="inappropriate_content">
                  Inappropriate Content
                </option>
                <option value="fake_review">Fake Review</option>
                <option value="spam">Spam</option>
                <option value="harassment">Harassment</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() =>
                  setReportModal({
                    show: false,
                    reviewId: null,
                    reason: "inappropriate_content",
                  })
                }
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  reportReview(reportModal.reviewId, reportModal.reason)
                }
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleReviewManagement;
